import { DateRange } from 'bold-ui'
import { AgeRangeValue } from 'components/date/AgeRange'
import { EquipeSelectModel, RadioOption } from 'components/form'
import { CiapCidSelectModel } from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { GrupoCondicaoSelectModel } from 'components/form/field/select/GrupoCondicaoSelectField/GrupoCondicaoSelectField'
import { RacaCorSelectModel } from 'components/form/field/select/RacaCorSelectField'
import { startOfDay, subMonths } from 'date-fns'
import {
  AcompanhamentoCondicaoSaudeTematicaEnum,
  AgeRangeType,
  FaixaEtariaEnum,
  FaixaEtariaInput,
  GrupoCondicaoEnum,
  IvcfSubgrupoPerguntaEnum,
  PageParams,
  PeriodoCadastroIndividualEnum,
  RiscoVulnerabilidadeIvcfEnum,
  SexoEnum,
} from 'graphql/types.generated'

import { AcompanhamentoFilterModel } from '../../model/model-acompanhamentoFilter'

export const ACOMPANHAMENTO_CONDICAO_SAUDE_ATMOSPHERE_PATH = 'acompanhamento-condicao-saude'

export enum AgeRangeFilter {
  TODAS_FAIXAS = 'TODAS_FAIXAS',
  CRIANCA = 'CRIANCA',
  ADOLESCENTE = 'ADOLESCENTE',
  ADULTO = 'ADULTO',
  IDOSO = 'IDOSO',
  OUTRA = 'OUTRA',
}

export interface AcompanhamentoCondicaoSaudeFormModel extends AcompanhamentoFilterModel {
  unidadeResponsavelId?: ID
  equipeResponsavelId?: ID
  equipeResponsavel?: EquipeSelectModel
  isCoordenador?: boolean
  microareas?: string[]
  foraarea?: boolean
  naoinformada?: boolean
  grupoCondicao?: GrupoCondicaoSelectModel[]
  problemasCondicoes?: CiapCidSelectModel[]
  ativoListaProblema?: boolean
  naoIncluidoListaProblema?: boolean
  periodoUltimoAtendimento?: DateRange
  // TODO (Royal Flush): Remover isIvcfListing quando listas temáticas forem desflagueadas
  isIvcfListing?: boolean
  ivcf?: IvcfFilterModel
  tria?: TriaFilterModel
  tipoVulnerabilidadeSelected?: TipoVulnerabilidadeEnum
  pageParams?: PageParams
  racaCor?: RacaCorSelectModel
}

export const faixaEtariaRecord: Partial<Record<FaixaEtariaEnum, FaixaEtariaInput>> = {
  [FaixaEtariaEnum.CRIANCA]: { idadeMaxima: 10, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.ADOLESCENTE]: { idadeMinima: 11, idadeMaxima: 19, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.ADULTO]: { idadeMinima: 20, idadeMaxima: 59, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.IDOSO]: { idadeMinima: 60, tipoFaixaEtaria: AgeRangeType.ANOS },
}

export interface IvcfFilterModel {
  riscoVulnerabilidade?: RiscoVulnerabilidadeIvcfEnum[]
  subgrupo?: IvcfSubgrupoPerguntaEnum[]
  pontuacao?: AgeRangeValue
  buscaPorPontuacao?: boolean
  exibirApenasCidadaosIdososSemIvcf?: boolean
}

export interface TriaFilterModel {
  periodoRegistro?: PeriodoCadastroIndividualEnum
  resultadosTria?: ResultadosTriaEnum
}

export enum TipoVulnerabilidadeEnum {
  TRIA = 'TRIA',
  IVCF = 'IVCF',
}

export enum ResultadosTriaEnum {
  RISCO_DE_INSEGURANCA_ALIMENTAR = 'RISCO_DE_INSEGURANCA_ALIMENTAR',
  SEM_REGISTRO_TRIA_NO_PERIODO = 'SEM_REGISTRO_TRIA_NO_PERIODO',
}

export const resultadosTriaOptions: RadioOption<ResultadosTriaEnum>[] = [
  {
    value: ResultadosTriaEnum.RISCO_DE_INSEGURANCA_ALIMENTAR,
    label: 'Exibir apenas pessoas que vivem em domicílios em risco de insegurança alimentar',
  },
  {
    value: ResultadosTriaEnum.SEM_REGISTRO_TRIA_NO_PERIODO,
    label: 'Exibir apenas pessoas que vivem em domicílios sem registro da TRIA no período',
  },
]

export const periodoRegistroTriaRecord: Record<PeriodoCadastroIndividualEnum, string> = {
  [PeriodoCadastroIndividualEnum.SEIS_MESES]: 'Últimos 6 meses',
  [PeriodoCadastroIndividualEnum.DOZE_MESES]: 'Últimos 12 meses',
}

export const filterDefaultAcompanhamentoCondicaoSaude: Partial<Record<
  AcompanhamentoCondicaoSaudeTematicaEnum,
  AcompanhamentoCondicaoSaudeFormModel
>> = {
  GERAL: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: null,
    problemasCondicoes: null,
    ativoListaProblema: null,
    naoIncluidoListaProblema: null,
    identidadeGenero: null,
    sexo: null,
    tipoFaixaEtaria: FaixaEtariaEnum.TODAS_FAIXAS,
    faixaEtariaRange: null,
    racaCor: null,
  },
  GESTANTE_PUERPERA: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: null,
    problemasCondicoes: null,
    ativoListaProblema: null,
    naoIncluidoListaProblema: null,
    identidadeGenero: null,
    sexo: null,
    tipoFaixaEtaria: FaixaEtariaEnum.TODAS_FAIXAS,
    faixaEtariaRange: null,
    racaCor: null,
  },
  PESSOA_IDOSA: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: null,
    problemasCondicoes: null,
    ativoListaProblema: null,
    naoIncluidoListaProblema: null,
    identidadeGenero: null,
    sexo: null,
    tipoFaixaEtaria: FaixaEtariaEnum.IDOSO,
    faixaEtariaRange: null,
    ivcf: undefined,
    racaCor: null,
  },
  DIABETES: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: [{ grupo: GrupoCondicaoEnum.DIABETES }] as Partial<GrupoCondicaoSelectModel[]>,
    problemasCondicoes: null,
    ativoListaProblema: true,
    naoIncluidoListaProblema: null,
    identidadeGenero: null,
    sexo: null,
    tipoFaixaEtaria: FaixaEtariaEnum.TODAS_FAIXAS,
    faixaEtariaRange: null,
    racaCor: null,
  },
  HIPERTENSAO_ARTERIAL: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: [{ grupo: GrupoCondicaoEnum.HIPERTENSAO_ARTERIAL }] as Partial<GrupoCondicaoSelectModel[]>,
    problemasCondicoes: null,
    ativoListaProblema: true,
    naoIncluidoListaProblema: null,
    identidadeGenero: null,
    sexo: null,
    tipoFaixaEtaria: FaixaEtariaEnum.TODAS_FAIXAS,
    faixaEtariaRange: null,
    racaCor: null,
  },
  SAUDE_MULHER: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: null,
    problemasCondicoes: null,
    ativoListaProblema: null,
    naoIncluidoListaProblema: null,
    identidadeGenero: null,
    sexo: [SexoEnum.FEMININO, SexoEnum.INDETERMINADO],
    tipoFaixaEtaria: FaixaEtariaEnum.OUTRA,
    faixaEtariaRange: {
      tipoFaixaEtaria: AgeRangeType.ANOS,
      firstValue: 14,
      secondValue: 69,
    },
    racaCor: null,
  },
  DESENVOLVIMENTO_INFANTIL: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: null,
    problemasCondicoes: null,
    ativoListaProblema: null,
    naoIncluidoListaProblema: null,
    identidadeGenero: null,
    sexo: null,
    tipoFaixaEtaria: FaixaEtariaEnum.OUTRA,
    faixaEtariaRange: {
      tipoFaixaEtaria: AgeRangeType.ANOS,
      firstValue: 0,
      secondValue: 2,
    },
    racaCor: null,
  },
  SAUDE_BUCAL: {
    periodoUltimoAtendimento: {
      startDate: subMonths(startOfDay(new Date()), 36),
      endDate: startOfDay(new Date()),
    },
    grupoCondicao: null,
    problemasCondicoes: null,
    ativoListaProblema: null,
    naoIncluidoListaProblema: null,
    identidadeGenero: null,
    sexo: null,
    tipoFaixaEtaria: FaixaEtariaEnum.TODAS_FAIXAS,
    faixaEtariaRange: null,
    racaCor: null,
  },
}
